// /partners/new — form para crear un partner nuevo.
//
// Despues del create, mostramos un modal con las DOS API keys plaintext + el
// issuer_url. El admin debe copiarlas: la DB guarda solo sha256, si las
// pierde hay que rotar.

import { useState } from 'react';
import { ArrowLeft, Check } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AdminApiError,
  createPartnerApi,
  type PartnerCreateBody,
  type PartnerCreateResponse,
} from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import { fieldCls } from '@/lib/form-styles';
import { canWrite as canWriteSection } from '@/lib/access';

const PARTNER_ID_RE = /^[a-z][a-z0-9-]{1,62}[a-z0-9]$/;

interface FormState {
  partner_id: string;
  display_name: string;
  payment_provider_default: 'line' | 'mercadopago' | 'stripe';
  webhook_url: string;
}

function initialState(): FormState {
  return {
    partner_id: '',
    display_name: '',
    payment_provider_default: 'line',
    webhook_url: '',
  };
}

function CopyableField({ label, value, sensitive = false }: { label: string; value: string; sensitive?: boolean }): JSX.Element {
  const [copied, setCopied] = useState(false);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Ignorar — el usuario ya ve el valor para copiar manual.
    }
  }

  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-ink-muted uppercase tracking-wide">{label}</label>
      <div className="flex gap-2">
        <code
          className={`flex-1 rounded-md border border-surface-border dark:border-surface-dark-border bg-surface dark:bg-surface-dark px-3 py-2 text-xs font-mono break-all ${
            sensitive ? 'text-status-error' : ''
          }`}
        >
          {value}
        </code>
        <button
          type="button"
          onClick={copy}
          className="rounded-md border border-surface-border dark:border-surface-dark-border bg-surface dark:bg-surface-dark px-3 text-xs hover:bg-surface-hover dark:hover:bg-surface-dark-hover"
        >
          {copied ? <Check size={12} aria-label="copiado" /> : 'Copiar'}
        </button>
      </div>
    </div>
  );
}

function CreatedModal({
  result,
  onClose,
}: {
  result: PartnerCreateResponse;
  onClose: () => void;
}): JSX.Element {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-2xl rounded-xl border border-surface-border dark:border-surface-dark-border bg-canvas dark:bg-canvas-dark p-6 shadow-xl space-y-4">
        <header>
          <h2 className="text-lg font-semibold">Partner creado: {result.partner_id}</h2>
          <p className="mt-1 text-sm text-status-warning">
            <strong>Anotá estas API keys ahora</strong> — no se vuelven a mostrar. La DB guarda
            solo el hash. Si las perdés, hay que rotar.
          </p>
        </header>

        <div className="space-y-3">
          <CopyableField label="Partner ID" value={result.partner_id} />
          <CopyableField label="Issuer URL" value={result.issuer_url} />
          <CopyableField label="API Key (standard)" value={result.api_key} sensitive />
          <CopyableField label="API Key (admin scope)" value={result.api_key_admin} sensitive />
          {result.api_secret ? (
            <CopyableField label="api_secret (firma HMAC S2S)" value={result.api_secret} sensitive />
          ) : null}
        </div>

        {/* El partner ya nace con clave de firma; lo mostramos para que el admin
            sepa que el login OIDC funciona sin pasos extra. */}
        {result.jwks?.current ? (
          <p className="text-sm text-status-success">
            Claves de firma OIDC generadas (kid{' '}
            <code className="font-mono text-xs">{result.jwks.current.kid}</code>
            {result.jwks.current.alg ? `, ${result.jwks.current.alg}` : ''}). El partner ya puede
            emitir tokens.
          </p>
        ) : null}

        {result.warnings.length > 0 ? (
          <div
            role="alert"
            className="rounded-lg border border-status-warning/40 bg-status-warning/10 p-3 text-sm space-y-2"
          >
            <p className="font-medium text-status-warning">
              El partner se creó, pero quedaron credenciales sin generar:
            </p>
            <ul className="list-disc pl-5 text-xs space-y-1">
              {result.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
            <p className="text-xs text-ink-muted">
              Reintentá desde el detalle del partner, sección Credenciales.
            </p>
          </div>
        ) : null}

        <footer className="flex justify-end gap-2 pt-2 border-t border-surface-border dark:border-surface-dark-border">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md bg-brand-primary px-4 py-2 text-sm font-medium text-white hover:bg-brand-primary-hover"
          >
            Entendido, las anoté
          </button>
        </footer>
      </div>
    </div>
  );
}

export default function PartnerNew(): JSX.Element {
  const session = useSession();
  const canWrite = canWriteSection(session, 'partners');
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [form, setForm] = useState<FormState>(initialState);
  const [formError, setFormError] = useState<string | null>(null);
  const [created, setCreated] = useState<PartnerCreateResponse | null>(null);

  const createMut = useMutation({
    mutationFn: async () => {
      const body: PartnerCreateBody = {
        partner_id: form.partner_id.trim(),
        display_name: form.display_name.trim(),
        payment_provider_default: form.payment_provider_default,
      };
      const url = form.webhook_url.trim();
      if (url) body.webhook_url = url;
      return createPartnerApi(body);
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['admin-partners'] });
      setCreated(data);
    },
    onError: (e) =>
      setFormError(e instanceof AdminApiError ? e.message : 'No pudimos crear el partner.'),
  });

  function onSubmit(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setFormError(null);
    const slug = form.partner_id.trim();
    if (!PARTNER_ID_RE.test(slug)) {
      setFormError('partner_id debe ser slug lowercase (a-z, dígitos, guiones), no empezar/terminar en guion. 3-64 chars.');
      return;
    }
    if (!form.display_name.trim()) {
      setFormError('display_name es requerido.');
      return;
    }
    createMut.mutate();
  }

  if (!canWrite) {
    return (
      <div className="space-y-4">
        <h1 data-help-page="partners.new" data-help="partners.new" className="text-2xl font-semibold">Nuevo partner</h1>
        <p className="text-sm text-status-error">Solo el rol "admin" puede crear partners.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <header>
        <Link to="/partners" className="inline-flex items-center gap-1 text-sm text-ink-muted hover:underline">
          <ArrowLeft size={14} aria-hidden="true" />
          Volver a Partners
        </Link>
        <h1 data-help-page="partners.new" data-help="partners.new" className="mt-2 text-2xl font-semibold">Nuevo partner</h1>
        <p className="text-sm text-ink-muted">
          Crea un partner con dos API keys (standard + admin). El plaintext se muestra una sola
          vez al final.
        </p>
      </header>

      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="partner_id">
            Partner ID (slug)
          </label>
          <input
            id="partner_id"
            type="text"
            value={form.partner_id}
            onChange={(e) => setForm({ ...form, partner_id: e.target.value })}
            placeholder="lpf-play"
            className={fieldCls({ size: 'lg', mono: true })}
            required
            autoFocus
          />
          <p className="mt-1 text-xs text-ink-muted">
            Lowercase a-z, dígitos, guiones. 3-64 chars. Inmutable después de crear.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="display_name">
            Nombre visible
          </label>
          <input
            id="display_name"
            type="text"
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
            placeholder="LPF Play"
            className={fieldCls({ size: 'lg' })}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="payment_provider_default">
            Proveedor de pago por defecto
          </label>
          <select
            id="payment_provider_default"
            value={form.payment_provider_default}
            onChange={(e) =>
              setForm({
                ...form,
                payment_provider_default: e.target.value as FormState['payment_provider_default'],
              })
            }
            className={fieldCls({ size: 'lg' })}
          >
            <option value="line">Line (Argentina)</option>
            <option value="mercadopago">MercadoPago</option>
            <option value="stripe">Stripe</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="webhook_url">
            Webhook URL (opcional)
          </label>
          <input
            id="webhook_url"
            type="url"
            value={form.webhook_url}
            onChange={(e) => setForm({ ...form, webhook_url: e.target.value })}
            placeholder="https://api.partner.example.com/webhooks/authsys"
            className={fieldCls({ size: 'lg', mono: true })}
          />
          <p className="mt-1 text-xs text-ink-muted">
            Si lo dejás vacío, no enviamos webhooks. Se puede setear después en el edit.
          </p>
        </div>

        {formError ? (
          <div role="alert" className="rounded-md border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error">
            {formError}
          </div>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={() => navigate('/partners')}
            className="rounded-md border border-surface-border dark:border-surface-dark-border bg-surface dark:bg-surface-dark px-4 py-2 text-sm hover:bg-surface-hover dark:hover:bg-surface-dark-hover"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={createMut.isPending}
            className="rounded-md bg-brand-primary px-4 py-2 text-sm font-medium text-white hover:bg-brand-primary-hover disabled:opacity-50"
          >
            {createMut.isPending ? 'Creando…' : 'Crear partner'}
          </button>
        </div>
      </form>

      {created ? (
        <CreatedModal
          result={created}
          onClose={() => {
            setCreated(null);
            navigate(`/partners/${encodeURIComponent(created.partner_id)}/edit`);
          }}
        />
      ) : null}
    </div>
  );
}
